import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { FC } from 'react';
import CellValue from '@app/shared/components/cell-value/CellValue';
import {
  amountRules,
  decimalCalculatedViewRules,
  enteredPercentageViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import { useLocale } from '@app/core/hooks/useLocale';
import { getWaterfallCaseYearValue } from '@app/core/store/pwerm2-calculation-slice-selectors';
import { getCalculatedProjectValueInPercent } from '@app/shared/formatters';
import TableSectionValuesPerForecast from '../../components/TableSectionValuesPerForecast';

interface ExitEquitySectionProps {
  columnsCount: number;
}

const ExitEquitySection: FC<ExitEquitySectionProps> = ({ columnsCount }) => {
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedResults);
  const { l } = useLocale();

  return (
    <>
      <tr data-testid="exit-equity-header-row">
        <th className="table-primary__cell--header">{l('_ExitEquity')}</th>
        <th colSpan={columnsCount - 1} className="table-primary__cell--header" />
      </tr>
      <TableSectionValuesPerForecast
        title={l('_WeightedAverageTotalExitEquity')}
        renderSpacer={false}
        renderCell={({ caseItem, forecast }) => (
          <CellValue
            {...amountRules}
            value={getWaterfallCaseYearValue(
              results,
              caseItem.caseId,
              forecast.forecastId,
              (year) => year.weightedTotalEquityProceeds
            )}
            dataTestId={`weighted-exit-equity-cell-${caseItem.caseId}-${forecast.forecastId}`}
          />
        )}
      />
      <TableSectionValuesPerForecast
        title={l('_CostOfTotalEquity')}
        renderSpacer={false}
        renderCell={({ caseItem, forecast }) => (
          <CellValue
            {...enteredPercentageViewRules}
            value={getCalculatedProjectValueInPercent(
              Number(
                getWaterfallCaseYearValue(
                  results,
                  caseItem.caseId,
                  forecast.forecastId,
                  (year) => year.cumulativeCostOfEquityPc
                )
              )
            )}
            dataTestId={`exit-equity-cost-of-total-equity-cell-${caseItem.caseId}-${forecast.forecastId}`}
          />
        )}
      />
      <TableSectionValuesPerForecast
        title={l('_DiscountFactor')}
        renderSpacer={false}
        renderCell={({ caseItem, forecast }) => (
          <CellValue
            {...decimalCalculatedViewRules}
            value={getWaterfallCaseYearValue(
              results,
              caseItem.caseId,
              forecast.forecastId,
              (year) => year.cumulativeDiscountFactor
            )}
            dataTestId={`exit-equity-discount-factor-cell-${caseItem.caseId}-${forecast.forecastId}`}
          />
        )}
      />
      <TableSectionValuesPerForecast
        title={l('_PVOfExitEquity')}
        renderSpacer
        renderCell={({ caseItem, forecast }) => (
          <CellValue
            {...amountRules}
            value={getWaterfallCaseYearValue(
              results,
              caseItem.caseId,
              forecast.forecastId,
              (year) => year.weightedTotalEquityFairValue
            )}
            dataTestId={`pv-of-exit-equity-cell-${caseItem.caseId}-${forecast.forecastId}`}
          />
        )}
      />
    </>
  );
};

export default ExitEquitySection;
